import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #lastSelectTime = 0;

  /**
   * Handles a product select event by either selecting the corresponding slide (same product, no reorder) or replacing the gallery (cross-product).
   * Preserves 3.2.1 "no-reorder + thumbnail auto-center" behavior via slideshow.select + scrollIntoView.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        if (detail.newProduct) {
          const newMediaGallery = html.querySelector('media-gallery');
          if (!newMediaGallery) return;
          this.replaceWith(newMediaGallery);
          return;
        }

        let mediaId = detail.resource?.featured_media?.id;
        if (!mediaId) {
          const variantScript = html.querySelector('variant-picker script[type="application/json"]');
          if (variantScript?.textContent) {
            try {
              const variant = JSON.parse(variantScript.textContent);
              mediaId = variant?.featured_media?.id;
            } catch {}
          }
        }

        if (!mediaId || !this.slideshow || !this.media) {
          const newMediaGallery = html.querySelector('media-gallery');
          if (newMediaGallery) this.replaceWith(newMediaGallery);
          return;
        }

        const targetIndex = this.media.findIndex(
          (el) => el.querySelector('[data-media-id]')?.dataset.mediaId === `${mediaId}`,
        );
        if (targetIndex === -1) {
          const newMediaGallery = html.querySelector('media-gallery');
          if (newMediaGallery) this.replaceWith(newMediaGallery);
          return;
        }

        const now = Date.now();
        const recent = now - this.#lastSelectTime < 400;
        this.#lastSelectTime = now;

        if (this.slideshow.hasAttribute('transitioning') || recent) {
          this.slideshow.select(targetIndex, undefined, { animate: false });
        } else {
          this.slideshow.select(targetIndex);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
